import React from 'react'

import {
  Header,
  type HeaderActions,
  type HeaderData,
  type HeaderLabels,
} from '@/components/organisms/Header/Header.tsx'
import {
  Navigation,
  type NavigationAction,
  type NavigationLabels,
  type NavigationOptions,
  type NavigationState,
} from '@/components/organisms/Navigation/Navigation.tsx'
import {
  TeamSidepanel,
  type TeamSidepanelAction,
  type TeamSidepanelData,
  type TeamSidepanelLabels,
  type TeamSidepanelNavigation,
  type TeamSidepanelTimeFormat,
} from '@/components/organisms/TeamSidepanel/TeamSidepanel.tsx'
import { cn } from '@/lib/utils.ts'

export type MainLayoutTimeFormat = TeamSidepanelTimeFormat

export type MainLayoutNavigation = NavigationOptions

export interface MainLayoutLabels {
  header: HeaderLabels
  navigation: NavigationLabels
  sidepanel: TeamSidepanelLabels
  automationPause: string
}

export interface MainLayoutState extends NavigationState {
  television?: boolean
  selectedTeamId?: TeamSidepanelNavigation['selected']
  teamSortBy: TeamSidepanelNavigation['sortBy']
  teamFilterByCarType?: TeamSidepanelNavigation['filterByCarType']
}

export interface MainLayoutData {
  header: HeaderData
  sidepanel: TeamSidepanelData
  automationPauseDelay?: string
}

export interface MainLayoutAction extends HeaderActions, NavigationAction, TeamSidepanelAction {}

export interface MainLayoutProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, MainLayoutAction, MainLayoutTimeFormat {
  labels: MainLayoutLabels
  data: MainLayoutData
  state: MainLayoutState
  navigation: MainLayoutNavigation
}

export const MainLayout = React.forwardRef<HTMLDivElement, MainLayoutProps>(
  (
    {
      labels,
      data,
      state,
      navigation,
      children,
      onBackToPiloteClicked,
      onBaseChanged,
      onMobileRequestToggled,
      onTeamSortByChanged,
      onNavigationChanged,
      onStartingLocationChanged,
      onSearchChanged,
      onNavigationSortChanged,
      onTeamSelected,
      onTelevisionToggled,
      onSuperPower,
      onViewChanged,
      onWaitTimeToggled,
      onChangeAssignationState,
      onNotificationSoundToggled,
      timeAgoFormatter,
    },
    ref
  ) => (
    <div ref={ref}>
      {data.automationPauseDelay && (
        <div className="absolute top-6 left-1/2 z-50 inline-flex w-[340px] -translate-x-1/2 items-center justify-center gap-2.5 rounded-[8px] bg-red-800 px-14 py-6 shadow-[0px_1px_1.41px_0px_rgba(0,0,0,0.20)]">
          <p className="text-center text-[16px] leading-[150%] font-medium text-white uppercase">
            {labels.automationPause} <br />* {data.automationPauseDelay} *
          </p>
        </div>
      )}

      <Header
        className="fixed top-0 z-10 h-fit"
        labels={labels.header}
        data={data.header}
        navigation={{ television: { state: state.television ? 'active' : 'default' } }}
        onBackToPiloteClicked={onBackToPiloteClicked}
        onMobileRequestToggled={onMobileRequestToggled}
        onTelevisionToggled={onTelevisionToggled}
        onSuperPower={onSuperPower}
        onWaitTimeToggled={onWaitTimeToggled}
        onChangeAssignationState={onChangeAssignationState}
        onNotificationSoundToggled={onNotificationSoundToggled}
      />

      {!state.television && (
        <Navigation
          {...navigation}
          className="fixed top-[49px] h-fit w-full"
          labels={labels.navigation}
          state={state}
          onBaseChanged={onBaseChanged}
          onNavigationChanged={onNavigationChanged}
          onStartingLocationChanged={onStartingLocationChanged}
          onSearchChanged={onSearchChanged}
          onNavigationSortChanged={onNavigationSortChanged}
          onViewChanged={onViewChanged}
        />
      )}

      <main
        className={cn('fixed h-screen overflow-y-auto', state.television ? 'top-[49px]' : 'top-[167px]')}
        style={{
          width: state.television ? '' : 'calc(100vw - 144px)',
        }}>
        {children}
      </main>

      {!state.television && (
        <TeamSidepanel
          className="fixed top-[49px] right-0"
          navigation={{
            selected: state.selectedTeamId,
            sortBy: state.teamSortBy,
            filterByCarType: state.teamFilterByCarType,
          }}
          labels={labels.sidepanel}
          data={data.sidepanel}
          timeAgoFormatter={timeAgoFormatter}
          onTeamSortByChanged={onTeamSortByChanged}
          onTeamSelected={onTeamSelected}
        />
      )}
    </div>
  )
)
